import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A = 2 points
 * B = 1 point
 * C = 0 points
 */

const scoreMap = {
  0: 2,
  1: 1,
  2: 0,
};

const quizData = [
  {
    question: "After eating a regular meal, I usually feel…",
    options: [
      "Comfortable, light, and satisfied",
      "Sometimes heavy or bloated",
      "Very uncomfortable, sleepy, or uneasy",
    ],
  },
  {
    question: "My bowel movements are generally…",
    options: [
      "Regular and easy",
      "Irregular — sometimes fine, sometimes not",
      "Frequently uncomfortable or unpredictable",
    ],
  },
  {
    question: "My appetite through the day feels…",
    options: [
      "Clear and predictable",
      "Up and down",
      "Confusing — either no hunger or sudden strong hunger",
    ],
  },
  {
    question: "I notice that I usually eat my meals…",
    options: [
      "Calmly and with attention",
      "Sometimes rushed, sometimes mindful",
      "Very rushed, distracted, or while multitasking",
    ],
  },
  {
    question: "My energy levels after lunch are usually…",
    options: [
      "Stable and functional",
      "Slightly low or inconsistent",
      "Very low — I feel sleepy or drained",
    ],
  },
  {
    question: "I experience issues like gas, acidity, or indigestion…",
    options: ["Rarely", "Occasionally", "Frequently"],
  },
  {
    question: "My relationship with cravings feels like…",
    options: [
      "I can notice them and choose consciously",
      "I give in sometimes",
      "They feel strong and hard to control",
    ],
  },
  {
    question: "My water intake through the day is…",
    options: [
      "Regular and spaced out",
      "Inconsistent",
      "Either very little or too much at once",
    ],
  },
  {
    question: "Late-night eating for me is…",
    options: ["Rare or very light", "Occasional", "Frequent or heavy"],
  },
  {
    question: "My sleep quality lately feels…",
    options: [
      "Restful and refreshing",
      "Light or interrupted",
      "Poor or unrefreshing",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
    <div
      className="
        relative
        overflow-hidden
        rounded-[34px]
        max-w-5xl
        w-full
        h-[620px]
        shadow-[0_30px_80px_rgba(20,10,0,.12)]
      "
    >
      <img
        src="/assets/knowyourself/quiz/gut.jpg"
        alt="Gut Health Assessment"
        className="
          w-full
          h-full
          object-cover
          brightness-[.84]
          saturate-[.9]
        "
      />

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/90
          via-black/35
          to-transparent
        "
      />

      <div className="absolute inset-x-0 bottom-0 p-6">
        <div
          className="
            rounded-[28px]
            bg-white/10
            backdrop-blur-2xl
            border
            border-white/10
            p-8
          "
        >
          <div
            className="
              inline-flex
              px-4
              py-2
              rounded-full
              bg-white/10
              backdrop-blur-xl
              border
              border-white/10
              text-white
              text-xs
              tracking-[.08em]
              mb-5
            "
          >
            WELLNESS ASSESSMENT
          </div>

          <h1
            className="
              text-[38px]
              md:text-[56px]
              leading-[1.05]
              text-white
              mb-5
            "
          >
            Gut Health Assessment
          </h1>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-4
            "
          >
            Discover how well your digestive system
            is supporting your energy, comfort,
            metabolism, and overall wellbeing.
          </p>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-8
            "
          >
            Think about your typical experience over
            the last few weeks and choose the option
            that feels most true most of the time.
          </p>

          <Button
            variant="primary"
            text="Start Assessment"
            onClick={onStart}
          />
        </div>
      </div>
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans) => sum + scoreMap[ans],
    0
  );

  let title = "";
  let resultText = "";

  if (totalScore >= 15) {
    title = "🌿 Gut in Relative Balance";

    resultText =
      "Your digestion appears to be supporting your energy, wellbeing, and daily functioning fairly well. Your body is giving reasonably clear hunger and fullness signals. Continue supporting your gut with consistent routines and mindful eating habits.";
  } else if (totalScore >= 8) {
    title = "🌱 Mild to Moderate Imbalance";

    resultText =
      "Your digestive system may be reacting to stress, irregular schedules, inconsistent eating patterns, or lifestyle habits. Occasional discomfort is a signal that your body may benefit from more regular meals, slower eating, and better recovery habits.";
  } else {
    title = "🌧️ Gut Under Stress";

    resultText =
      "Your digestive system may currently be struggling with stress, poor routines, rushed eating habits, or insufficient recovery. Supporting digestion through simplicity, warmth, consistency, rest, and mindful eating can make a significant difference.";
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
      <div
        className="
          relative
          overflow-hidden
          rounded-[34px]
          max-w-5xl
          w-full
          h-[620px]
          shadow-[0_30px_80px_rgba(20,10,0,.12)]
        "
      >
        <img
          src="/assets/knowyourself/quiz/result.jpg"
          alt="Assessment Result"
          className="
            w-full
            h-full
            object-cover
            brightness-[.84]
            saturate-[.9]
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/90
            via-black/35
            to-transparent
          "
        />

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div
            className="
              rounded-[28px]
              bg-white/10
              backdrop-blur-2xl
              border
              border-white/10
              p-8
            "
          >
            <div
              className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-white/10
                backdrop-blur-xl
                border
                border-white/10
                text-white
                text-xs
                tracking-[.08em]
                mb-5
              "
            >
              YOUR RESULT
            </div>

            <h2
              className="
                text-[34px]
                md:text-[52px]
                leading-[1.05]
                text-white
                mb-4
              "
            >
              {title}
            </h2>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-4
              "
            >
              Total Score: {totalScore} / 20
            </p>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-8
                max-w-3xl
              "
            >
              {resultText}
            </p>

            <Button
              variant="primary"
              text="Retake Assessment"
              onClick={onRetake}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const GutHealthQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default GutHealthQuiz;
